import React, { useState } from 'react';
import { Button, Input } from '@/components/atoms';
import { z } from 'zod';

const createOutputSchema = z.object({
  name: z.string().min(1, 'Output name is required'),
  description: z.string().optional(),
  quantity: z.number().positive('Quantity must be positive'),
  unit: z.string().min(1, 'Unit is required'),
});

type CreateOutput = z.infer<typeof createOutputSchema>;

interface OutputFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const OutputForm: React.FC<OutputFormProps> = ({ onSuccess, onCancel }) => {
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isLoading] = useState(false);

  const [formData, setFormData] = useState<CreateOutput>({
    name: '',
    description: '',
    quantity: 0,
    unit: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});

    try {
      createOutputSchema.parse(formData);
      // TODO: Wire up to API when output endpoints are available
      onSuccess?.();
    } catch (err) {
      if (err && typeof err === 'object' && 'errors' in err) {
        const zodError = err as { errors: Array<{ path: (string | number)[]; message: string }> };
        const errMap: Record<string, string> = {};
        zodError.errors.forEach((e) => {
          const field = String(e.path[0]);
          errMap[field] = e.message;
        });
        setValidationErrors(errMap);
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred');
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <p className="text-error-700">{error}</p>
        </div>
      )}

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-2">
          Output Name *
        </label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter output name"
          disabled={isLoading}
        />
        {validationErrors.name && (
          <p className="text-error-600 text-sm mt-1">{validationErrors.name}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-neutral-700 mb-2">
            Quantity *
          </label>
          <Input
            id="quantity"
            name="quantity"
            type="number"
            value={formData.quantity}
            onChange={handleChange}
            placeholder="Enter quantity"
            disabled={isLoading}
            min="0"
          />
          {validationErrors.quantity && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.quantity}</p>
          )}
        </div>

        <div>
          <label htmlFor="unit" className="block text-sm font-medium text-neutral-700 mb-2">
            Unit *
          </label>
          <Input
            id="unit"
            name="unit"
            value={formData.unit}
            onChange={handleChange}
            placeholder="e.g., kg, liters"
            disabled={isLoading}
          />
          {validationErrors.unit && (
            <p className="text-error-600 text-sm mt-1">{validationErrors.unit}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-neutral-700 mb-2">
          Description
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter description (optional)"
          disabled={isLoading}
          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-neutral-100 disabled:cursor-not-allowed"
          rows={3}
        />
      </div>

      <div className="flex gap-2 justify-end">
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Create'} Output
        </Button>
      </div>
    </form>
  );
};
